import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Input,
  Button,
  Table,
  UncontrolledDropdown,
  UncontrolledAlert,
  UncontrolledTooltip,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalBody,
  ModalFooter
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { usePocketBase } from "../../../PocketBaseContext";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import debounce from 'lodash.debounce';

// Helper function for truncating text
const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

const CustomerList = () => {
  document.title = "Customers | Reform Panel";
  const pb = usePocketBase();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    kurumsallik: "",
    personnel: "",
  });
  const [sortField, setSortField] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [alert, setAlert] = useState({ show: false, color: "", message: "" });

  useEffect(() => {
    fetchCustomers();
    fetchPersonnel();
  }, []);

  useEffect(() => {
    debouncedFetchCustomers();
  }, [filters, sortField, sortOrder]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      let filter = "";
      if (filters.search) {
        filter += `(name ~ "${filters.search}" || customer_brand_description ~ "${filters.search}")`;
      }
      if (filters.kurumsallik) {
        filter += filter ? ` && ` : '';
        filter += `kurumsallik = "${filters.kurumsallik}"`;
      }
      if (filters.personnel) {
        filter += filter ? ` && ` : '';
        filter += `customer_contact ?~ "${filters.personnel}"`;
      }

      const resultList = await pb.collection('customers').getList(1, 50, {
        sort: `${sortOrder === "desc" ? "-" : ""}${sortField}`,
        expand: 'customer_contact',
        filter: filter,
      });
      setCustomers(resultList.items);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setLoading(false);
    }
  };

  const debouncedFetchCustomers = useCallback(debounce(fetchCustomers, 300), [filters, sortField, sortOrder]);

  const fetchPersonnel = async () => {
    try {
      const resultList = await pb.collection('personel').getList(1, 50, {
        sort: 'name',
      });
      setPersonnel(resultList.items);
    } catch (error) {
      console.error("Error fetching personnel:", error);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleDeleteCustomer = async () => {
    if (customerToDelete) {
      try {
        await pb.collection('customers').delete(customerToDelete.id);
        setDeleteModal(false);
        fetchCustomers();
      } catch (error) {
        console.error("Error deleting customer:", error);
      }
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleEditSelected = () => {
    if (selectedCustomers.length === 1) {
      navigate(`/reform-Customers/detail/${selectedCustomers[0]}`);
    } else if (selectedCustomers.length > 1) {
      setAlert({
        show: true,
        color: "warning",
        message: "Lütfen düzenlemek için sadece bir müşteri seçin."
      });
    } else {
      setAlert({
        show: true,
        color: "info",
        message: "Lütfen düzenlemek için bir müşteri seçin."
      });
    }
  };

  const handleSelectAllCustomers = (e) => {
    if (e.target.checked) {
      setSelectedCustomers(customers.map(customer => customer.id));
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedCustomers.map(id => pb.collection('customers').delete(id)));
      fetchCustomers();
      setSelectedCustomers([]);
      setBulkDeleteModal(false);
    } catch (error) {
      console.error("Error deleting customers:", error);
    }
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const closeAlert = () => {
    setAlert({ show: false, color: "", message: "" });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Müşteri Listesi" pageTitle="Müşteriler" />
        {alert.show && (
          <UncontrolledAlert color={alert.color} toggle={closeAlert}>
            {alert.message}
          </UncontrolledAlert>
        )}
        <Row>
          <Col lg={3}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Filtreler</h4>
              </CardHeader>
              <CardBody>
                <div>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder="Müşteri Ara..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange("search", e.target.value)}
                  />
                </div>
                <div className="mt-3">
                  <Select
                    value={filters.kurumsallik ? { value: filters.kurumsallik, label: filters.kurumsallik === 'kurumsal' ? 'Kurumsal' : 'Bireysel' } : null}
                    onChange={(selected) => handleFilterChange("kurumsallik", selected ? selected.value : "")}
                    options={[
                      { value: "", label: "Tüm Tipler" },
                      { value: "kurumsal", label: "Kurumsal" },
                      { value: "bireysel", label: "Bireysel" }
                    ]}
                    placeholder="Müşteri Tipi Seç"
                    isClearable
                  />
                </div>
                <div className="mt-3">
                  <Select
                    value={personnel.find(person => person.id === filters.personnel)}
                    onChange={(selected) => handleFilterChange("personnel", selected ? selected.value : "")}
                    options={[
                      { value: "", label: "Tüm Personel" },
                      ...personnel.map(person => ({
                        value: person.id,
                        label: `${person.name} ${person.surname}`
                      }))
                    ]}
                    placeholder="Personel Seç"
                    isClearable
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg={9}>
            <Card>
              <CardBody>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="search-box">
                      <Input
                        type="text"
                        className="form-control search"
                        placeholder="Müşteri Ara..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange("search", e.target.value)}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </div>
                  <div className="col-md-8 text-md-end">
                    {selectedCustomers.length > 0 && (
                      <>
                        <Button color="danger" className="me-2" onClick={() => setBulkDeleteModal(true)}>
                          <i className="ri-delete-bin-line align-bottom me-1"></i> Seçilenleri Sil
                        </Button>
                        <Button color="primary" className="me-2" onClick={handleEditSelected}>
                          <i className="ri-edit-line align-bottom me-1"></i> Seçileni Düzenle
                        </Button>
                      </>
                    )}
                    <Link to="/reform-Customers/add" className="btn btn-success">
                      <i className="ri-add-line align-bottom me-1"></i> Müşteri Ekle
                    </Link>
                  </div>
                </div>
                <div className="table-responsive">
                  <Table className="table-hover table-nowrap mb-0 align-middle">
                    <thead className="table-light text-muted">
                      <tr>
                        <th scope="col" style={{ width: "46px" }}>
                          <div className="form-check">
                            <Input
                              className="form-check-input"
                              type="checkbox"
                              id="checkAll"
                              onChange={handleSelectAllCustomers}
                              checked={selectedCustomers.length === customers.length}
                            />
                          </div>
                        </th>
                        <th onClick={() => handleSort("name")}>Müşteri Adı</th>
                        <th onClick={() => handleSort("customer_brand_description")}>Marka Açıklaması</th>
                        <th onClick={() => handleSort("customer_address")}>Adres</th>
                        <th>İletişim Kişisi</th>
                        <th onClick={() => handleSort("kurumsallik")}>Müşteri Tipi</th>
                        <th onClick={() => handleSort("created")}>Oluşturulma Tarihi</th>
                        <th onClick={() => handleSort("updated")}>Güncellenme Tarihi</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map((customer) => (
                        <tr key={customer.id} onClick={() => navigate(`/reform-Customers/detail/${customer.id}`)} style={{ cursor: 'pointer' }}>
                          <th scope="row" onClick={(e) => e.stopPropagation()}>
                            <div className="form-check">
                              <Input
                                className="form-check-input"
                                type="checkbox"
                                onChange={() => handleSelectCustomer(customer.id)}
                                checked={selectedCustomers.includes(customer.id)}
                              />
                            </div>
                          </th>
                          <td>{customer.name}</td>
                          <td>{truncateText(customer.customer_brand_description, 30)}</td>
                          <td>
                            <div id={`address-${customer.id}`}>
                              {truncateText(customer.customer_address, 50)}
                            </div>
                            <UncontrolledTooltip 
                              target={`address-${customer.id}`}
                              placement="top"
                            >
                              {customer.customer_address}
                            </UncontrolledTooltip>
                          </td>
                          <td>{customer.expand?.customer_contact?.map(contact => contact.name).join(", ")}</td>
                          <td>
                            <span className={`badge bg-${customer.kurumsallik === 'kurumsal' ? 'success' : 'primary'}`}>
                              {customer.kurumsallik === 'kurumsal' ? 'Kurumsal' : 'Bireysel'}
                            </span>
                          </td>
                          <td>{new Date(customer.created).toLocaleDateString()}</td>
                          <td>{new Date(customer.updated).toLocaleDateString()}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <UncontrolledDropdown>
                              <DropdownToggle tag="button" className="btn btn-soft-secondary btn-sm">
                                <i className="ri-more-fill" />
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem onClick={() => navigate(`/reform-Customers/detail/${customer.id}`)}>
                                  <i className="ri-eye-fill align-bottom me-2 text-muted"></i> Görüntüle
                                </DropdownItem>
                                <DropdownItem onClick={() => navigate(`/reform-Customers/detail/${customer.id}`)}>
                                  <i className="ri-pencil-fill align-bottom me-2 text-muted"></i> Düzenle
                                </DropdownItem>
                                <DropdownItem divider />
                                <DropdownItem onClick={() => {
                                  setCustomerToDelete(customer);
                                  setDeleteModal(true);
                                }}>
                                  <i className="ri-delete-bin-fill align-bottom me-2 text-danger"></i> Sil
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="row align-items-center mt-2 g-3 text-center text-sm-start">
                  <div className="col-sm">
                    <div className="text-muted">Showing <span className="fw-semibold">10</span> of <span className="fw-semibold">{customers.length}</span> Results</div>
                  </div>
                  <div className="col-sm-auto">
                    <Pagination className="pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
                      <PaginationItem disabled><PaginationLink href="#">Önceki</PaginationLink></PaginationItem>
                      <PaginationItem active><PaginationLink href="#">1</PaginationLink></PaginationItem>
                      <PaginationItem disabled><PaginationLink href="#">Sonraki</PaginationLink></PaginationItem>
                    </Pagination>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteCustomer}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Modal isOpen={bulkDeleteModal} toggle={() => setBulkDeleteModal(!bulkDeleteModal)}>
        <ModalBody>
          Seçili müşterileri silmek istediğinizden emin misiniz?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setBulkDeleteModal(false)}>İptal</Button>
          <Button color="danger" onClick={handleBulkDelete}>Sil</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CustomerList;